/* ============================================
   Artemis Theme — Apparel Stock
   ============================================
   Shared inventory helpers for apparel. One rule,
   two consumers: the PDP per-size availability grid
   (apparel-pdp.js) and the designer's per-size
   quantity caps (designer.js).

   The stock cap deliberately does NOT trust
   `inventory_management`. On the storefront that
   field can render blank even for a tracked variant,
   which made tracked sizes look unlimited (you could
   type 4,000,000 with no cap). Instead we infer
   tracked-ness from quantity + availability + policy,
   which Shopify reports reliably:
     - oversell allowed (policy 'continue')  -> no cap
     - qty > 0                               -> cap at qty
     - qty 0 & available                     -> untracked, no cap
     - qty 0 & not available                 -> tracked & empty, cap 0
   ============================================ */
(function () {
  'use strict';

  var LOW_STOCK_THRESHOLD = 5;

  // Stock cap for a variant.
  //   null -> uncapped (not tracked, or oversell allowed)
  //   >=0  -> on-hand count to cap quantity at (0 = out of stock)
  function variantStock(v) {
    if (!v) return null;
    if (v.inventoryPolicy === 'continue') return null;
    var qty = parseInt(v.inventoryQuantity, 10);
    if (isFinite(qty) && qty > 0) return qty;
    // qty is 0/blank: an untracked variant still reports available:true,
    // a tracked-but-empty one reports available:false.
    if (v.available) return null;
    return 0;
  }

  // In / low / out status from a cap. lowAt defaults to LOW_STOCK_THRESHOLD.
  function stockStatus(stock, lowAt) {
    var low = (lowAt == null) ? LOW_STOCK_THRESHOLD : lowAt;
    if (stock == null) return 'in'; // uncapped
    if (stock <= 0) return 'out';
    if (stock <= low) return 'low';
    return 'in';
  }

  function findOptionIndex(product, predicate) {
    var opts = (product && product.options) || [];
    for (var i = 0; i < opts.length; i++) {
      if (predicate(String(opts[i]).toLowerCase())) return i;
    }
    return -1;
  }

  function colorIndexOf(product) {
    return findOptionIndex(product, function (n) { return n === 'color' || n === 'colour'; });
  }

  function sizeIndexOf(product) {
    var sizeIdx = findOptionIndex(product, function (n) { return n === 'size' || n.indexOf('size') >= 0; });
    if (sizeIdx >= 0) return sizeIdx;
    var colorIdx = colorIndexOf(product);
    var opts = (product && product.options) || [];
    for (var i = 0; i < opts.length; i++) {
      if (i !== colorIdx) return i;
    }
    return -1;
  }

  // Canonical apparel size order (small -> big). Variants often arrive
  // alphabetized; we re-sort to this scale. Unknowns fall to the end.
  var SIZE_ORDER = [
    'xxs', '2xs',
    'xs', 'x-small', 'extra small', 'extra-small',
    's', 'sm', 'small',
    'm', 'md', 'medium',
    'l', 'lg', 'large',
    'xl', 'x-large', 'extra large', 'extra-large',
    'xxl', '2xl', '2x-large', '2x large',
    'xxxl', '3xl', '3x-large', '3x large',
    'xxxxl', '4xl', '4x-large', '4x large',
    'xxxxxl', '5xl', '5x-large', '5x large',
    '6xl', '6x-large', '6x large',
  ];

  // Length modifiers (Tall, Big, Long) that pair with a base size. Each pushes
  // the size into a later group so e.g. all regular sizes sort before all Tall
  // ones, while the base size still orders within the group.
  var SIZE_MODIFIERS = ['tall', 'big', 'long'];

  function sizeRank(size) {
    var key = String(size == null ? '' : size).trim().toLowerCase();
    var idx = SIZE_ORDER.indexOf(key);
    if (idx >= 0) return idx;
    // Peel off a trailing/leading length modifier and rank by the base size,
    // grouping each modifier after the plain sizes.
    for (var m = 0; m < SIZE_MODIFIERS.length; m++) {
      var mod = SIZE_MODIFIERS[m];
      var re = new RegExp('(^' + mod + '\\b|\\b' + mod + '$)', 'g');
      if (re.test(key)) {
        // Match the base separator-insensitively so "x-large"/"x large" and
        // "2x-large"/"2x large" all resolve regardless of how the size is spelled.
        var base = key.replace(re, '').replace(/[\s-]+/g, '');
        var baseRank = SIZE_ORDER.length;
        for (var b = 0; b < SIZE_ORDER.length; b++) {
          if (SIZE_ORDER[b].replace(/[\s-]+/g, '') === base) { baseRank = b; break; }
        }
        return SIZE_ORDER.length * (m + 1) + baseRank;
      }
    }
    return SIZE_ORDER.length * (SIZE_MODIFIERS.length + 2);
  }

  // Sizes for one color of a product, in canonical size order, each carrying
  // its stock cap and In/Low/Out status.
  function sizesForColor(product, color) {
    var colorIdx = colorIndexOf(product);
    var sizeIdx = sizeIndexOf(product);
    var matching = (product.variants || []).filter(function (v) {
      if (colorIdx < 0) return true;
      return v.options[colorIdx] === color;
    });
    return matching.map(function (v) {
      var stock = variantStock(v);
      return {
        size: sizeIdx >= 0 ? v.options[sizeIdx] : 'One Size',
        variantId: v.id,
        priceCents: v.priceCents,
        available: v.available,
        stock: stock,
        status: stockStatus(stock),
      };
    }).sort(function (a, b) {
      var ra = sizeRank(a.size);
      var rb = sizeRank(b.size);
      if (ra !== rb) return ra - rb;
      return 0;
    });
  }

  window.ApparelStock = {
    LOW_STOCK_THRESHOLD: LOW_STOCK_THRESHOLD,
    variantStock: variantStock,
    stockStatus: stockStatus,
    colorIndexOf: colorIndexOf,
    sizeIndexOf: sizeIndexOf,
    sizeRank: sizeRank,
    sizesForColor: sizesForColor,
  };
})();
